import React, { useState, useMemo } from 'react';
import { motion } from 'framer-motion';
import { PackageOpen, AlertCircle, CheckCircle2, Clock, Search } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { usePinto } from '../hooks/usePinto';
import { usePintoSchedule } from '../hooks/usePintoSchedule';
import { formatThaiDateShort } from '../utils/dateFormat';
import Skeleton from '../components/ui/Skeleton';
import EmptyState from '../components/ui/EmptyState';
import './PintoPage.css';

const PintoPage = () => {
  const { member } = useAuth();
  const { packages, activePackage, metrics, selectPackage, loading: pintoLoading } = usePinto(member);
  const { schedules, loading: schedulesLoading } = usePintoSchedule(activePackage?.id);

  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');

  const categories = useMemo(() => {
    const cats = new Set();
    schedules.forEach(s => {
      if (!s.is_extra_order && s.menu_items?.category) {
        cats.add(s.menu_items.category);
      }
    });
    return ['all', ...Array.from(cats)];
  }, [schedules]);

  // Group schedules
  const getMealWeight = (type) => {
    if (type === 'breakfast' || type === 'meal_1') return 1;
    if (type === 'lunch' || type === 'meal_2') return 2;
    if (type === 'dinner' || type === 'meal_3') return 3;
    return 99;
  };

  const processedSchedules = [...schedules].filter(s => !s.is_extra_order).sort((a, b) => {
    const dateDiff = new Date(a.delivery_date) - new Date(b.delivery_date);
    if (dateDiff !== 0) return dateDiff;
    return getMealWeight(a.meal_type) - getMealWeight(b.meal_type);
  });
  processedSchedules.forEach((s, idx) => {
    s.global_meal_number = idx + 1;
  });

  const filteredSchedules = processedSchedules.filter(s => {
    const matchSearch = s.menu_items?.name?.toLowerCase().includes(searchQuery.toLowerCase()) || true;
    const searchMatch = searchQuery ? s.menu_items?.name?.toLowerCase().includes(searchQuery.toLowerCase()) : true;
    const catMatch = selectedCategory === 'all' || s.menu_items?.category === selectedCategory;
    return searchMatch && catMatch;
  });

  const groupedSchedules = filteredSchedules.reduce((acc, curr) => {
    const date = curr.delivery_date;
    if (!acc[date]) acc[date] = { items: [], macros: { cal: 0, pro: 0, carb: 0, fat: 0 } };
    acc[date].items.push(curr);
    if (curr.menu_items) {
      const qty = curr.quantity || 1;
      acc[date].macros.cal += (curr.menu_items.calories || 0) * qty;
      acc[date].macros.pro += (curr.menu_items.protein || 0) * qty;
      acc[date].macros.carb += (curr.menu_items.carbs || 0) * qty;
      acc[date].macros.fat += (curr.menu_items.fat || 0) * qty;
    }
    return acc;
  }, {});

  const totalPackageMacros = Object.values(groupedSchedules).reduce((acc, day) => {
    acc.cal += day.macros.cal;
    acc.pro += day.macros.pro;
    acc.carb += day.macros.carb;
    acc.fat += day.macros.fat;
    return acc;
  }, { cal: 0, pro: 0, carb: 0, fat: 0 });

  const sortedDates = Object.keys(groupedSchedules).sort((a, b) => new Date(b) - new Date(a));

  const isScheduleCompleted = (schedule) => {
    if (schedule.kitchen_status === 'completed') return true;
    const deliveryDate = new Date(schedule.delivery_date);
    const now = new Date();
    
    const dDay = new Date(deliveryDate);
    dDay.setHours(0, 0, 0, 0);
    const today = new Date(now);
    today.setHours(0, 0, 0, 0);

    if (dDay < today) return true;
    if (dDay > today) return false;

    // Today: check by time
    // Pinto meals are delivered together in a single round.
    // Assuming the round is finished by 13:00 (1 PM).
    const currentHour = now.getHours();
    if (currentHour >= 13) {
      return true;
    }
    
    // If before 13:00, consider them still pending
    return false;
  };

  const getDisplayPackageName = (name) => {
    if (!name) return 'แพ็กเกจปิ่นโต';
    const lowerName = name.toLowerCase();
    
    if (lowerName.includes('muscle') || name.includes('สร้างกล้าม') || name.includes('เพิ่มกล้าม')) {
      return 'โปรโมชั่นเพิ่มกล้าม';
    }
    
    if (name.includes('ปิ่นโต') || name.includes('ผูกปิ่นโต')) {
      return 'โปรโมชั่นผูกปิ่นโต';
    }
    
    // Fallback: take the first two words if it starts with โปรโมชั่น (e.g. "โปรโมชั่น ทดลอง")
    const cleanName = name.replace(/^- /, '').trim();
    const parts = cleanName.split(' ');
    if (parts.length >= 2 && parts[0].includes('โปรโมชั่น')) {
      return `${parts[0]} ${parts[1]}`;
    }
    
    return cleanName;
  };

  const hasPendingDeliveries = schedules && schedules.some(schedule => {
    if (schedule.kitchen_status === 'completed') return false;
    const deliveryDate = new Date(schedule.delivery_date);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    deliveryDate.setHours(0, 0, 0, 0);
    return deliveryDate >= today;
  });

  const getDisplayStatus = (pkg, met) => {
    if (actualRemaining <= 0) {
      if (hasPendingDeliveries) return { text: 'กำลังใช้งาน', className: 'active' };
      return { text: 'ครบกำหนด', className: 'completed' };
    }
    if (pkg.status === 'active') return { text: 'กำลังใช้งาน', className: 'active' };
    if (pkg.status === 'paused') return { text: 'หยุดชั่วคราว', className: 'paused' };
    if (pkg.status === 'completed') return { text: 'ครบกำหนด', className: 'completed' };
    return { text: pkg.status, className: pkg.status };
  };

  const actualConsumed = schedules ? schedules.filter(s => !s.is_extra_order && isScheduleCompleted(s)).length : 0;
  const actualRemaining = metrics ? Math.max(0, metrics.totalMeals - actualConsumed) : 0;
  const progressPct = metrics?.totalMeals ? Math.min(100, (actualConsumed / metrics.totalMeals) * 100) : 0;

  const isLowBalance = metrics && actualRemaining > 0 && actualRemaining <= 10;
  const isEmptyBalance = metrics && actualRemaining <= 0 && !hasPendingDeliveries;

  if (pintoLoading) {
    return (
      <div className="pinto-page">
        <Skeleton height="300px" radius="24px" />
        <div style={{ marginTop: '24px' }}>
          <Skeleton height="80px" radius="16px" />
          <Skeleton height="80px" radius="16px" />
        </div>
      </div>
    );
  }

  if (!activePackage) {
    return (
      <div className="pinto-page">
        <EmptyState 
          icon={<PackageOpen size={48} />}
          title="ไม่พบแพ็กเกจปิ่นโต"
          description="คุณยังไม่มีแพ็กเกจปิ่นโตที่กำลังใช้งาน หรือแพ็กเกจหมดอายุแล้ว"
        />
      </div>
    );
  }

  return (
    <div className="pinto-page">
      {/* Packages Tabs */}
      {packages.length > 1 && (
        <div className="package-tabs">
          {packages.map(pkg => (
            <button 
              key={pkg.id}
              className={`package-tab ${activePackage.id === pkg.id ? 'active' : ''}`}
              onClick={() => selectPackage(pkg)}
            >
              {getDisplayPackageName(pkg.package_name)}
            </button>
          ))}
        </div>
      )}

      {/* Status Widget */}
      <motion.div 
        className={`pinto-status-widget ${isLowBalance ? 'low-balance' : ''} ${isEmptyBalance ? 'empty-balance' : ''}`}
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
      >
        <div className="widget-header">
          <h2 className="package-name">{getDisplayPackageName(activePackage.package_name)}</h2>
          <div className={`status-badge ${getDisplayStatus(activePackage, metrics).className}`}>
            {getDisplayStatus(activePackage, metrics).text}
          </div>
        </div>

        <div className="clean-progress-widget">
          <div className="progress-ring-container">
            <svg viewBox="0 0 100 100" className="progress-ring">
              <defs>
                <linearGradient id="progressGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stopColor="var(--color-brand)" />
                  <stop offset="100%" stopColor="var(--color-brand-light, #10b981)" />
                </linearGradient>
              </defs>
              <circle className="ring-bg" cx="50" cy="50" r="42" />
              <circle 
                className="ring-progress" 
                cx="50" cy="50" r="42" 
                style={{ strokeDashoffset: `${264 - (264 * progressPct) / 100}` }}
              />
            </svg>
            <div className="meals-count-clean">
              <span className="remaining">{actualRemaining}</span>
              <div className="meta-text">
                <span className="total">ทั้งหมด {metrics?.totalMeals || 0}</span>
                <span className="label">มื้ออาหาร</span>
              </div>
            </div>
          </div>
        </div>

        {isLowBalance && activePackage.status === 'active' && (
          <div className="warning-banner animate-pulse">
            <AlertCircle size={18} />
            <span>ใกล้หมดแล้ว! สนใจต่ออายุแพ็กเกจไหมคะ ?</span>
          </div>
        )}

        {isEmptyBalance && (
          <div className="warning-banner danger animate-pulse">
            <AlertCircle size={18} />
            <span>แพ็กเกจของคุณหมดแล้ว! (มื้อครบกำหนด)</span>
          </div>
        )}

        <div className="package-details-grid">
          <div className="detail-item">
            <span className="detail-label">วันเริ่ม</span>
            <span className="detail-value">{activePackage.start_date ? formatThaiDateShort(activePackage.start_date) : '-'}</span>
          </div>
          <div className="detail-item">
            <span className="detail-label">มื้อทั้งหมด</span>
            <span className="detail-value">{metrics?.totalMeals || 0} มื้อ</span>
          </div>
          <div className="detail-item highlight">
            <span className="detail-label">มื้อที่เหลือ</span>
            <span className="detail-value">{actualRemaining} มื้อ</span>
          </div>
        </div>
      </motion.div>

      {/* Timeline Section */}
      <div className="pinto-history-section">
        <div className="section-header-with-filters">
          <h3 className="section-title">มื้ออาหารของคุณ</h3>
          <div className="filter-controls">
            <div className="search-input-wrapper">
              <Search size={16} className="search-icon" />
              <input 
                type="text" 
                placeholder="ค้นหาชื่อเมนู..." 
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="menu-search-input"
              />
            </div>
            {categories.length > 1 && (
              <div className="category-select-wrapper">
                <select 
                  value={selectedCategory} 
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  className="category-select"
                >
                  <option value="all">ทุกหมวดหมู่</option>
                  {categories.filter(c => c !== 'all').map(c => (
                    <option key={c} value={c}>{c === 'main' ? 'เมนูหลัก' : c}</option>
                  ))}
                </select>
              </div>
            )}
          </div>
        </div>
        
        {schedulesLoading ? (
          <div className="timeline-loading">
            <Skeleton height="60px" radius="12px" />
            <Skeleton height="60px" radius="12px" />
          </div>
        ) : sortedDates.length > 0 ? (
          <>
            <div className="package-nutrition-summary">
              <h4>โภชนาการรวมของแพ็กเกจนี้</h4>
              <div className="macros-row">
                <span className="macro cal">🔥 {totalPackageMacros.cal} Kcal</span>
                <span className="macro pro">🥩 P: {totalPackageMacros.pro}g</span>
                <span className="macro carb">🍚 C: {totalPackageMacros.carb}g</span>
                <span className="macro fat">🥑 F: {totalPackageMacros.fat}g</span>
              </div>
            </div>

            <div className="meal-history-list">
              {sortedDates.map((date, i) => {
                const dayGroup = groupedSchedules[date];
                return (
                  <div key={date} className="daily-meal-group">
                    <div className="daily-meal-header">
                      <span className="delivery-date">{formatThaiDateShort(date)}</span>
                      <div className="macros-row small">
                        <span className="macro cal">{dayGroup.macros.cal} Kcal</span>
                        <span className="macro pro">P:{dayGroup.macros.pro}g</span>
                        <span className="macro carb">C:{dayGroup.macros.carb}g</span>
                        <span className="macro fat">F:{dayGroup.macros.fat}g</span>
                      </div>
                    </div>
                    
                    <div className="daily-meal-cards">
                      {dayGroup.items.slice().reverse().map((schedule, j) => (
                        <motion.div 
                          key={schedule.id || j}
                          className="meal-history-card"
                          initial={{ opacity: 0, y: 20 }}
                          whileInView={{ opacity: 1, y: 0 }}
                          viewport={{ once: true, margin: "-20px" }}
                          transition={{ duration: 0.4 }}
                        >
                          <div className="meal-card-content">
                            <div className="meal-card-header">
                              <div className="meal-title-group">
                                <span className="meal-name-main">{schedule.menu_items?.name || 'เมนูที่เลือก'} {schedule.quantity > 1 && `(x${schedule.quantity})`}</span>
                                {schedule.menu_items?.category && (
                                  <span className="category-badge">
                                    {schedule.menu_items.category === 'main' ? 'เมนูหลัก' : schedule.menu_items.category}
                                  </span>
                                )}
                              </div>
                              <span className={`delivery-status ${isScheduleCompleted(schedule) ? 'success' : 'pending'}`}>
                                {isScheduleCompleted(schedule) ? 'ส่งสำเร็จ' : 'รอจัดส่ง'}
                              </span>
                            </div>
                            
                            <div className="meal-type-row">
                              <span className="meal-type-label">
                                มื้อที่ {schedule.global_meal_number} {schedule.meal_type === 'lunch' ? '(มื้อกลางวัน)' : schedule.meal_type === 'dinner' ? '(มื้อเย็น)' : ''}
                              </span>
                            </div>
                            
                            {schedule.menu_items && (
                              <div className="macros-row mt-2">
                                <span className="macro cal">{schedule.menu_items.calories} Kcal</span>
                                <span className="macro pro">P: {schedule.menu_items.protein}g</span>
                                <span className="macro carb">C: {schedule.menu_items.carbs}g</span>
                                <span className="macro fat">F: {schedule.menu_items.fat}g</span>
                              </div>
                            )}
                          </div>
                        </motion.div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        ) : (
          <div className="empty-history">
            ยังไม่มีการจัดตารางมื้ออาหาร
          </div>
        )}
      </div>
    </div>
  );
};

export default PintoPage;
